import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import List from '../../../common/ListsOfComponents/List';
import BaseEditableItem from '../../../common/BaseEditableItem/BaseEditableItem';
import ActionsButton from '../../../common/buttons/ActionsButton/ActionsButton';
import SectionWrapper from '../../../common/ComposableWrappers/SectionWrapper/SectionWrapper';
import ValidationErrorLabel from '../../../common/ValidationErrorLabel/ValidationErrorLabel';
import JobAlertModal from '../../../common/ModalWindows/JobAlertModal/JobAlertModal';
import DeleteDialog from '../../../common/ModalWindows/DeleteDialog';
import { createNewJobAlert, deleteJobAlert, editJobAlert, getAllJobAlerts } from '../../../../actions/jobAlerts';
import { appNotificationsConfigs } from '../../../../utils/constants';
import { useNotifications } from '../../../../utils/hooks';

export default function JobAlerts() {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const alerts = useSelector((state) => state.jobAlerts.alerts);

  const createPending = useSelector((state) => state.jobAlerts.createPending);
  const updatePending = useSelector((state) => state.jobAlerts.updatePending);
  const deletePending = useSelector((state) => state.jobAlerts.deletePending);
  const createError = useSelector((state) => state.jobAlerts.createError);
  const updateError = useSelector((state) => state.jobAlerts.updateError);
  const deleteError = useSelector((state) => state.jobAlerts.deleteError);

  useNotifications(
    createPending,
    createError,
    appNotificationsConfigs.CREATE_SUCCESS(t),
    appNotificationsConfigs.CREATE_ERROR(createError)
  );

  useNotifications(
    updatePending,
    updateError,
    appNotificationsConfigs.UPDATE_SUCCESS(t),
    appNotificationsConfigs.UPDATE_ERROR(updateError)
  );

  useNotifications(
    deletePending,
    deleteError,
    appNotificationsConfigs.DELETE_SUCCESS(t),
    appNotificationsConfigs.DELETE_ERROR(deleteError)
  );

  const actionsTypes = {
    CREATE: 'create',
    EDIT: 'edit',
    CLOSE: ''
  };

  const createHandler = (data) => dispatch(createNewJobAlert(data));

  const editHandler = (data) => dispatch(editJobAlert(data));

  const deleteHandler = (id) => dispatch(deleteJobAlert(id));

  const modalWindowCreators = {
    CREATE: () => ({
      type: actionsTypes.CREATE,
      title: t('alerts.modal.create_title'),
      confirmHandler: createHandler,
      data: {}
    }),
    EDIT: (alert) => ({
      type: actionsTypes.EDIT,
      title: t('alerts.modal.edit_title'),
      confirmHandler: editHandler,
      data: alert
    }),
    CLOSE: () => ({
      type: actionsTypes.CLOSE,
      title: '',
      confirmHandler: () => {},
      data: {}
    })
  };

  const [open, setOpen] = useState(modalWindowCreators.CLOSE());
  const [deleteModal, setDeleteModal] = useState(null);

  useEffect(() => {
    dispatch(getAllJobAlerts());
  }, []);

  const frequencyStrings = {
    daily: `${t('alerts.modal.daily_label')} Email`,
    weekly: `${t('alerts.modal.weekly_label')} Email`
  };

  return (
    <SectionWrapper
      mainTitle={t('alerts.job_alerts.main_title')}
      secondaryInfo={t('alerts.job_alerts.secondary_title')}
      extraInfo={`(${alerts.length}/5)`}
    >
      <List
        items={alerts}
        renderItem={(alert) => (
          <BaseEditableItem
            key={alert.Id}
            disabled={!alert.IsActive}
            mainTitle={alert.Keyword}
            secondaryTitle={alert.Location}
            description={frequencyStrings[alert.Frequency]}
            editHandler={() => setOpen(modalWindowCreators.EDIT(alert))}
            deleteHandler={() => setDeleteModal(alert?.Id)}
          />
        )}
      />
      {alerts.length < 5 ? (
        <ActionsButton
          activityName="createAlert"
          clickHandler={() => setOpen(modalWindowCreators.CREATE())}
          isReverted
          withSizeCorrection
        />
      ) : (
        <ValidationErrorLabel message={t('alerts.job_alerts.info_message')} />
      )}
      <JobAlertModal
        type={open.type}
        title={open.title}
        closeHandler={() => setOpen(modalWindowCreators.CLOSE())}
        confirmHandler={(data) => {
          open.confirmHandler(data);
          setOpen(modalWindowCreators.CLOSE());
        }}
        data={open.data}
      />
      <DeleteDialog
        open={deleteModal}
        closeHandler={() => setDeleteModal(null)}
        confirmHandler={() => {
          deleteHandler(deleteModal);
          setDeleteModal(null);
        }}
        config={{
          title: t('dashboard.modal.delete.alert'),
          text: t('dashboard.modal.delete.confirmalert')
        }}
      />
    </SectionWrapper>
  );
}
